import {dialogEvents} from "@/data-views/dialogs";
import {auth} from "@/firebase";
import {useCookie} from "@/@core/composable/useCookie";
import axios from "axios";
import {jwtDecode} from "jwt-decode";
import Bugsnag from "@bugsnag/js";

// Get base url
const baseUrl = import.meta.env.VITE_API_ENDPOINT;

// Create config
const config = {
  baseURL: baseUrl, // url = base url + request url
  timeout: 540000,
  withCredentials: true,
  crossDomain: true,
  keepAlive: true,
};

async function refreshAccessToken() {
  const refreshToken = useCookie("refreshToken").value; // Assuming you store refreshToken
  if (!refreshToken) {
    // No refresh token, redirect to login
    console.error("No refresh token available, redirecting to login");
    if (!window.location.pathname.includes('/login')) {
      window.location.href = "/login";
    }
    return null;
  }

  try {
    const response = await fetch("/api/auth/refresh", {
      method: "POST",
      headers: {"Content-Type": "application/json"},
      body: JSON.stringify({refreshToken}),
      credentials: "include"
    });

    if (!response.ok) {
      throw new Error(`Failed to refresh token: ${response.status} ${response.statusText}`);
    }

    const {accessToken, refreshToken: newRefreshToken} = await response.json();

    // Save new tokens
    useCookie("accessToken").value = accessToken;
    auth.accessToken = accessToken;
    useCookie("refreshToken").value = newRefreshToken;
    auth.refreshToken = newRefreshToken;

    return accessToken;
  } catch (error) {
    console.error("Token refresh failed:", error);
    
    // Clear invalid tokens
    useCookie("accessToken").value = null;
    useCookie("refreshToken").value = null;
    auth.accessToken = null;
    auth.refreshToken = null;
    
    // Redirect to login on refresh token failure
    if (!window.location.pathname.includes('/login')) {
      console.error("Refresh token is invalid, redirecting to login");
      window.location.href = "/login";
    }
    
    return null;
  }
}

const service = axios.create(config);

// Request interceptors
service.interceptors.request.use(
  async (config) => {
    try {
      let accessToken = auth.accessToken ?? useCookie("accessToken").value;

      if (accessToken) {
        try {
          const decoded = jwtDecode(accessToken);
          if (!decoded || typeof decoded.exp !== "number") {
            throw new Error("Invalid access token format");
          }

          const now = Math.floor(Date.now() / 1000);
          const expiresIn = decoded.exp - now;

          // Refresh the token only if it's about to expire (e.g., in less than 30 seconds)
          if (expiresIn < 30) {
            accessToken = await refreshAccessToken();
          }
        } catch (decodeError: any) {
          console.error("Failed to decode access token:", decodeError);
          accessToken = await refreshAccessToken();
          Bugsnag.notify(decodeError);
        }

        config.headers.Authorization = `Bearer ${accessToken}`;
      }

      // Ensure CORS header is always set
      // config.headers["Access-Control-Allow-Origin"] = window.location.origin;
    } catch (error: any) {
      console.error("Interceptor error:", error);
      Bugsnag.notify(error);
    }

    return config;
  },
  async (error) => {
    console.error("Request failed:", error);
    return Promise.reject(error);
  }
);
// Response interceptors
service.interceptors.response.use(
  async (response) => {
    if (process.env.NODE_ENV === "development") {
      // console.info(response.config.url, response.data);
    }
    // Get data
    const res = response.data;
    // Handle
    if (response.status >= 200 && response.status < 300) {
      return res;
    } else if (response.status === 401) {
      // Prevent infinite redirect loops by checking current location
      if (!window.location.pathname.includes('/login')) {
        window.location.href = "/login";
      }
    } else {
      return await Promise.reject(response);
    }
  },
  async (error) => {
    console.error('error console:', error);
    if (error.code === "ERR_NETWORK") {
      document.dispatchEvent(new Event("network-error"));
    } else if (error.response.status === 417) {
      dialogEvents.ConfirmDialog.open(
        "This index does not exist yet, you will be redirected to the index creation page. Please wait 5 to 10 minutes after creating the index for the changes to take effect!",
        async () => {
          window.open(error.response.data.url, "_blank");
        },
        async () => {
          window.location.reload();
        },
      );
    } else if (error.response.status === 401) {
      const refreshResult = await auth.refreshToken();
      if (refreshResult.success) {
        return service.request(error.config);
      } else {
        console.error("Token refresh failed:", refreshResult.error);
        document.dispatchEvent(new Event("unauthorized"));
        // Prevent infinite redirect loops by checking current location
        if (!window.location.pathname.includes('/login')) {
          window.location.href = "/login";
        }
        // Don't report 401 errors to Bugsnag when they're properly handled by redirect
        return Promise.resolve(null);
      }
    }
    return await Promise.reject(error);
  },
);

export default service;
